import { useMemo, useState } from 'react'
import '../../styles/content/readyToGoContent.css'
import { ReactComponent as ShowMoreLessIcon } from '../../assets/svg/ShowMoreLessIcon.svg'
import { ReactComponent as ProgramIcon } from '../../assets/svg/ProgramIcon.svg'
import { ReactComponent as SurveyContentIcon } from '../../assets/svg/SurveyContentIcon.svg'
import { ReactComponent as JournalContentIcon } from '../../assets/svg/JournalContentIcon.svg'
import { useTranslation } from 'react-i18next'
import ContentCard, { ContentCardTooltips, ContentSkeleton, getContentKey, useRebuildTooltips } from './ToGo/ContentCard'

const PREVIEW_COUNT = 2
const ICON_FILL = '#413F5E'

const CONTENT_TYPES = [
    { code: 'programs', title: 'Programs', icon: <ProgramIcon fill={ICON_FILL} /> },
    { code: 'surveys', title: 'Surveys', icon: <SurveyContentIcon fill={ICON_FILL} /> },
    { code: 'journals', title: 'Journals', icon: <JournalContentIcon fill={ICON_FILL} /> },
]

const EMPTY = { programs: [], surveys: [], journals: [] }

const TopicHomeList = ({ content = EMPTY, onClick = () => {}, openCurrentlyRunningPrograms = () => {}, loading = true, version = '' }) => {
    const { t } = useTranslation()
    useRebuildTooltips(content)

    const sections = useMemo(
        () => CONTENT_TYPES.filter((type) => Array.isArray(content?.[type.code]) && content[type.code].length > 0).map((type) => ({ type, items: content[type.code] })),
        [content]
    )

    return (
        <div className="type-home-container">
            {loading && <ContentSkeleton />}
            {!loading && sections.length === 0 && (
                <div className="h-full w-full flex items-center justify-center py-[40px] rtg-empty">
                    <p className="text-[16px] text-darkPurple font-semibold">{t('No data found')}</p>
                </div>
            )}
            {!loading && (
                // Keyed by the data version so the stagger replays only when new
                // data arrives, never on a search keystroke.
                <div className="rtg-cards-animate" key={version}>
                    {sections.map(({ type, items }) => (
                        <ContentSection
                            key={type.code}
                            type={type}
                            items={items}
                            onSelect={(item) => onClick(item, type.title)}
                            onOpenRunning={openCurrentlyRunningPrograms}
                            t={t}
                        />
                    ))}
                </div>
            )}
            <ContentCardTooltips />
        </div>
    )
}

const ContentSection = ({ type, items, onSelect, onOpenRunning, t }) => {
    const [showMore, setShowMore] = useState(false)
    const needShowMoreButton = items.length > PREVIEW_COUNT
    const head = needShowMoreButton ? items.slice(0, PREVIEW_COUNT) : items
    const rest = needShowMoreButton ? items.slice(PREVIEW_COUNT) : []

    return (
        <div className="single-type-section">
            <div className="single-type-section-header">
                {type.icon}
                <p className="single-type-section-title" style={{ color: ICON_FILL, marginRight: 25 }}>
                    {t(type.title)}
                </p>
                <div className="single-type-dash" style={{ borderTopColor: '#BEBECE' }}></div>
            </div>
            <div className="single-type-section-items">
                {head.map((item, index) => (
                    <ContentCard
                        key={getContentKey(item, index)}
                        item={item}
                        index={index}
                        className="bg-[#F8F8FB] hover:bg-[#ECEBF8]"
                        iconFill={ICON_FILL}
                        onSelect={onSelect}
                        onOpenRunning={onOpenRunning}
                        t={t}
                    />
                ))}
            </div>
            {rest.length > 0 && (
                <div className="rtg-collapsible" data-open={showMore} aria-hidden={!showMore}>
                    <div className="rtg-collapsible-inner">
                        <div className="single-type-section-items">
                            {rest.map((item, index) => (
                                <ContentCard
                                    key={getContentKey(item, index + PREVIEW_COUNT)}
                                    item={item}
                                    index={index + PREVIEW_COUNT}
                                    className="bg-[#F8F8FB] hover:bg-[#ECEBF8]"
                                    iconFill={ICON_FILL}
                                    onSelect={onSelect}
                                    onOpenRunning={onOpenRunning}
                                    tabIndex={showMore ? 0 : -1}
                                    t={t}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {needShowMoreButton && (
                <div className="w-full justify-center">
                    <button type="button" className="showMoreLessButton" aria-expanded={showMore} onClick={() => setShowMore((prev) => !prev)}>
                        <span className={`rtg-chevron mb-[3px]${showMore ? ' is-flipped' : ''}`}>
                            <ShowMoreLessIcon />
                        </span>
                        <span className="ml-[8px]">{showMore ? t('showLess') : t('showMore')}</span>
                    </button>
                </div>
            )}
        </div>
    )
}

export default TopicHomeList
